import React from 'react';

import { Group, Select, Stack, Text } from '@mantine/core';
import type { PlotlyTypes } from 'visyn_core/plotly';
import { PlotlyComponent } from 'visyn_core/plotly';

import { ShmDistributionData } from '../generated/generatedOpenAPI';

interface DistributionOfSHMPlotProps {
  shmDistributionData: ShmDistributionData | undefined;
}

enum DistributionOption {
  AA = 'aa',
  DNA = 'dna',
}

export default function DistributionOfSHMPlot({ shmDistributionData }: DistributionOfSHMPlotProps) {
  const [shmDistributionOption, setShmDistributionOption] = React.useState<DistributionOption>(DistributionOption.AA);
  const data = React.useMemo(() => {
    if (!shmDistributionData) {
      return [];
    }
    let vhData = shmDistributionData.shmAa?.heavyChain ?? [];
    let vlData = shmDistributionData.shmAa?.lightChain ?? [];
    if (shmDistributionOption === DistributionOption.DNA) {
      vhData = shmDistributionData.shmDna?.heavyChain ?? [];
      vlData = shmDistributionData.shmDna?.lightChain ?? [];
    }
    return [
      { x: vhData, type: 'histogram', name: 'VH', opacity: 0.7, marker: { color: '#337ab7' }, xbins: { start: -0.5, size: 1 } },
      { x: vlData, type: 'histogram', name: 'VL', opacity: 0.7, marker: { color: '#15a154' }, xbins: { start: -0.5, size: 1 } },
    ];
  }, [shmDistributionData, shmDistributionOption]);

  return (
    <Stack gap="md">
      <Text size="xl">Distribution of SHM in Heavy and Light Chains</Text>
      <Group gap={7}>
        <Text>Distribution type:</Text>
        <Select
          data-testid="shm-distribution-option-select"
          value={shmDistributionOption}
          onChange={(value) => {
            if (typeof value === 'string') {
              setShmDistributionOption(value as DistributionOption);
            }
          }}
          data={[
            { label: 'AA', value: DistributionOption.AA },
            { label: 'DNA', value: DistributionOption.DNA },
          ]}
          w={100}
        />
      </Group>
      <div data-testid="shm-distribution-plot">
        <PlotlyComponent
          data={data as PlotlyTypes.Data[]}
          style={{ width: '100%' }}
          layout={{
            barmode: 'overlay',
            height: 320,
            margin: {
              l: 60,
              r: 10,
              b: 50,
              t: 20,
            },
            xaxis: {
              title: {
                text: `Number of SHM (${shmDistributionOption === DistributionOption.AA ? 'AA' : 'DNA'})`,
                font: { size: 16 },
              },
            },

            yaxis: {
              title: {
                text: 'Number of Chains',
                font: { size: 16 },
              },
            },
          }}
          config={{
            displaylogo: false,
            modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'autoScale2d', 'select2d'],
          }}
        />
      </div>
    </Stack>
  );
}
